/**
 * IVS face pins: streams are keyed by the same id Unity sends in VideoFaceLayout (Player.UserId / Intersect id).
 * IVS participant.userId should match that id (see ff-api video call token). We also register attributes.intersectId
 * so older tokens (username as UserId) still resolve for viewers.
 */

export function isLikelyScreenShareVideoTrack(track: MediaStreamTrack): boolean {
  if (track.kind !== 'video') {
    return false;
  }
  try {
    const s = track.getSettings() as MediaTrackSettings & { displaySurface?: string };
    if (s.displaySurface && s.displaySurface !== 'browser') {
      return true;
    }
    const w = s.width ?? 0;
    const h = s.height ?? 0;
    if (w >= 2560 || h >= 1600) {
      return true;
    }
  } catch {
    /* ignore */
  }
  const label = (track.label || '').toLowerCase();
  return (
    label.includes('screen') ||
    label.includes('display') ||
    label.includes('window') ||
    label.includes('monitor')
  );
}

export function normalizeFacePinUserId(id: string | undefined | null): string {
  return String(id ?? '').trim();
}

/** Same keys and same MediaStream references — skip setState when applyFacePinStreamForParticipant is a no-op. */
export function facePinStreamsRecordEqual(
  a: Record<string, MediaStream>,
  b: Record<string, MediaStream>
): boolean {
  const keys = Array.from(new Set([...Object.keys(a), ...Object.keys(b)]));
  for (const k of keys) {
    if (a[k] !== b[k]) {
      return false;
    }
  }
  return true;
}

export function getFacePinUserKey(participant: {
  userId?: string;
  info?: { userId?: string };
} | null | undefined): string {
  return normalizeFacePinUserId(participant?.userId ?? participant?.info?.userId);
}

/** All string keys we should store a remote face-pin stream under (Unity layout may match any of these). */
export function collectFacePinUserKeys(participant: any): string[] {
  const out = new Set<string>();
  const add = (v: unknown) => {
    const k = normalizeFacePinUserId(String(v ?? ''));
    if (k) {
      out.add(k);
    }
  };
  if (!participant) {
    return [];
  }
  add(participant.userId);
  add(participant.info?.userId);
  add(participant.attributes?.intersectId);
  add(participant.info?.attributes?.intersectId);
  add(participant.attributes?.username);
  add(participant.info?.attributes?.username);
  const primary = getFacePinUserKey(participant);
  if (primary) {
    out.add(primary);
  }
  return Array.from(out);
}

/** Register or clear face-pin MediaStream entries for every known identity key on this participant. */
export function applyFacePinStreamForParticipant(
  participant: any,
  faceMs: MediaStream | null,
  prev: Record<string, MediaStream>
): Record<string, MediaStream> {
  const next = { ...prev };
  const keys = collectFacePinUserKeys(participant);
  for (const k of keys) {
    delete next[k];
  }
  if (faceMs != null && faceMs.getTracks().length > 0) {
    for (const k of keys) {
      next[k] = faceMs;
    }
  }
  return next;
}

/** Match Unity `VideoFaceLayout` userId to IVS map keys (trim + case-insensitive fallback). */
export function resolveFacePinStreamForUserId(
  map: Record<string, MediaStream>,
  layoutUserId: string
): MediaStream | undefined {
  const id = normalizeFacePinUserId(layoutUserId);
  if (!id) {
    return undefined;
  }
  if (map[id]) {
    return map[id];
  }
  const lower = id.toLowerCase();
  for (const k of Object.keys(map)) {
    if (normalizeFacePinUserId(k).toLowerCase() === lower) {
      return map[k];
    }
  }
  return undefined;
}

/**
 * Clone camera video tracks for a second consumer (face pin `<video>`) so the same tracks can stay on `localVideoRef`.
 */
export function buildLocalFacePinStream(localStream: MediaStream): MediaStream | null {
  const ms = new MediaStream();
  for (const t of localStream.getVideoTracks()) {
    if (t.kind === 'video' && t.readyState === 'live' && !isLikelyScreenShareVideoTrack(t)) {
      try {
        ms.addTrack(t.clone());
      } catch {
        /* ignore */
      }
    }
  }
  return ms.getTracks().length > 0 ? ms : null;
}

/** Build a MediaStream of camera-only video tracks from IVS stage stream objects (with mediaStreamTrack). */
export function buildCameraFaceStreamFromIvsStreams(streams: any[]): MediaStream | null {
  const ms = new MediaStream();
  for (const s of streams) {
    const t = s?.mediaStreamTrack as MediaStreamTrack | undefined;
    if (t?.kind === 'video' && !isLikelyScreenShareVideoTrack(t)) {
      ms.addTrack(t);
    }
  }
  return ms.getTracks().length > 0 ? ms : null;
}
